// src/utils/tokenUtils.js

/**
 * Token Validation Utilities
 * 
 * Purpose: Check JWT expiration on frontend to prevent unnecessary API calls
 * 
 * Use this BEFORE making authenticated API requests to:
 * 1. Check if token exists
 * 2. Check if token is expired
 * 3. Redirect to login if needed
 */

/**
 * Decode JWT token (without verification)
 * 
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode base64 payload (middle part)
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * 
 * @param {string} token - JWT token
 * @returns {boolean} true if expired, false if valid
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  
  if (!decoded || !decoded.exp) {
    return true; // No expiration claim = treat as expired
  }
  
  // JWT exp is in seconds, Date.now() is in milliseconds
  const currentTime = Date.now() / 1000;
  
  return decoded.exp < currentTime;
};

/**
 * Get token expiration date
 * 
 * @param {string} token - JWT token
 * @returns {Date|null} Expiration date or null
 */
export const getTokenExpiration = (token) => {
  const decoded = decodeToken(token);
  
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  // Convert seconds to milliseconds
  return new Date(decoded.exp * 1000);
};

/**
 * Get remaining token validity in milliseconds
 * 
 * @param {string} token - JWT token
 * @returns {number} Milliseconds until expiration (negative if expired)
 */
export const getTokenRemainingTime = (token) => {
  const decoded = decodeToken(token);
  
  if (!decoded || !decoded.exp) {
    return 0;
  }
  
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  
  return expirationTime - currentTime;
};

/**
 * Check if token will expire soon (within given minutes)
 * 
 * @param {string} token - JWT token
 * @param {number} minutes - Minutes threshold (default: 5)
 * @returns {boolean} true if expiring soon
 */
export const isTokenExpiringSoon = (token, minutes = 5) => {
  const remainingTime = getTokenRemainingTime(token);
  const thresholdMs = minutes * 60 * 1000;
  
  return remainingTime > 0 && remainingTime < thresholdMs;
};

/**
 * Validate token and handle expiration
 * 
 * Returns status object:
 * - valid: Token is valid and not expired
 * - expired: Token is expired
 * - missing: No token found
 * - invalid: Token format is invalid
 * 
 * @returns {object} { status, message, shouldRedirect }
 */
export const validateAuthToken = () => {
  const token = localStorage.getItem('token');
  
  // No token
  if (!token) {
    return {
      status: 'missing',
      message: 'No authentication token found',
      shouldRedirect: true
    };
  }
  
  // Invalid token format
  if (!decodeToken(token)) {
    return {
      status: 'invalid',
      message: 'Invalid token format',
      shouldRedirect: true
    };
  }
  
  // Expired token
  if (isTokenExpired(token)) {
    const expiration = getTokenExpiration(token);
    return {
      status: 'expired',
      message: `Token expired at ${expiration?.toLocaleString()}`,
      shouldRedirect: true
    };
  }
  
  // Valid token
  return {
    status: 'valid',
    message: 'Token is valid',
    shouldRedirect: false
  };
};

/**
 * Clear expired tokens automatically
 * Call this on app initialization
 */
export const clearExpiredTokens = () => {
  const token = localStorage.getItem('token');
  
  if (token && isTokenExpired(token)) {
    console.log('Clearing expired token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return true;
  }
  
  return false;
};